import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

export default function WorkbenchPanel({
  title,
  children,
  onClose,
  compact,
  side = 'right',
  className = '',
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  compact: boolean;
  side?: 'left' | 'right';
  className?: string;
}) {
  const trigger = useRef<HTMLElement | null>(document.activeElement as HTMLElement);
  useEffect(() => {
    if (compact) return;
    const escape = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        document.querySelector('[data-state="open"][role="dialog"]')
      )
        return;
      if (side === 'left' && document.querySelector('.org-panel-right')) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [compact, onClose, side]);
  const header = (
    <div className="org-panel-header">
      <h2>{title}</h2>
      <button
        type="button"
        className="org-icon-button"
        aria-label={`Close ${title}`}
        onClick={onClose}
      >
        <X size={17} />
      </button>
    </div>
  );
  if (!compact)
    return (
      <aside className={`org-panel org-panel-${side} ${className}`} aria-label={title}>
        {header}
        {children}
      </aside>
    );
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="org-dialog-overlay" />
        <Dialog.Content
          className={`org-panel org-panel-drawer org-panel-${side} ${className}`}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            trigger.current?.focus();
          }}
        >
          <Dialog.Title className="org-sr-only">{title}</Dialog.Title>
          <Dialog.Description className="org-sr-only">
            View and manage the selected organizational information.
          </Dialog.Description>
          {header}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
